import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import config from '../../config';

export const RegisterForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    username: '',
    password: '',
    password_confirm: '',  // تغییر از confirmPassword به password_confirm
    email: '',
    role: 'customer',
  });
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!formData.phone) {
      errors.phone = 'شماره تلفن الزامی است';
    } else if (!/^09[0-9]{9}$/.test(formData.phone)) {
      errors.phone = 'شماره تلفن نامعتبر است (مثال: 09123456789)';
    }
    
    if (!formData.username) {
      errors.username = 'نام کاربری الزامی است';
    } else if (formData.username.length < 3) {
      errors.username = 'نام کاربری حداقل 3 کاراکتر باید باشد';
    }
    
    if (!formData.password) {
      errors.password = 'رمز عبور الزامی است';
    } else if (formData.password.length < 6) {
      errors.password = 'رمز عبور حداقل 6 کاراکتر باید باشد';
    }
    
    if (formData.password !== formData.password_confirm) {
      errors.password_confirm = 'رمز عبور با تکرار آن مطابقت ندارد';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // داده‌های مورد نیاز برای بک‌اند
      const submitData = {
        phone: formData.phone,
        username: formData.username,
        password: formData.password,
        password_confirm: formData.password_confirm,
        email: formData.email || '',
        role: formData.role,
      };
      
      console.log('Sending data:', submitData);
      
      const response = await fetch(config.getApiUrl('/accounts/register/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      
      const data = await response.json();
      console.log('Response:', data);
      
      if (response.ok) {
        setSuccessMessage('ثبت نام با موفقیت انجام شد! در حال انتقال به صفحه ورود...');
        
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // نمایش خطاهای سرور
        if (typeof data === 'object') {
          const errorMessages = Object.entries(data)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .join('\n');
          setError(errorMessages);
        } else {
          setError(data.message || data.detail || 'خطا در ثبت نام');
        }
      }
    } catch (err) {
      setError('مشکل در ارتباط با سرور. لطفاً دوباره تلاش کنید.');
      console.error('Register error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            ثبت نام در سولار پلتفرم
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            یا{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              وارد حساب خود شوید
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* شماره تلفن */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              شماره تلفن <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={formData.phone}
              onChange={handleChange}
              className={`mt-1 block w-full px-3 py-2 border ${
                formErrors.phone ? 'border-red-500' : 'border-gray-300'
              } rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="09123456789"
            />
            {formErrors.phone && (
              <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>
            )}
          </div>

          {/* نام کاربری */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              نام کاربری <span className="text-red-500">*</span>
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              value={formData.username}
              onChange={handleChange}
              className={`mt-1 block w-full px-3 py-2 border ${
                formErrors.username ? 'border-red-500' : 'border-gray-300'
              } rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="نام کاربری خود را وارد کنید"
            />
            {formErrors.username && (
              <p className="mt-1 text-xs text-red-500">{formErrors.username}</p>
            )}
          </div>

          {/* ایمیل */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              ایمیل (اختیاری)
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="example@email.com"
            />
          </div>

          {/* رمز عبور */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              رمز عبور <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  formErrors.password ? 'border-red-500' : 'border-gray-300'
                } rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {formErrors.password && (
              <p className="mt-1 text-xs text-red-500">{formErrors.password}</p>
            )}
          </div>

          {/* تکرار رمز عبور */}
          <div>
            <label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700">
              تکرار رمز عبور <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password_confirm"
                name="password_confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.password_confirm}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  formErrors.password_confirm ? 'border-red-500' : 'border-gray-300'
                } rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {formErrors.password_confirm && (
              <p className="mt-1 text-xs text-red-500">{formErrors.password_confirm}</p>
            )}
          </div>

          {/* نقش کاربری */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              نقش شما
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="customer">مشتری (خریدار تجهیزات)</option>
              <option value="installer">نصاب (متخصص نصب)</option>
              <option value="seller">فروشنده (فروش تجهیزات)</option>
            </select>
          </div>

          {/* پیام موفقیت */}
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          {/* پیام خطا */}
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <pre className="text-sm text-red-700 whitespace-pre-wrap font-sans">{error}</pre>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin ml-2" />
                  در حال ثبت نام...
                </>
              ) : (
                'ثبت نام'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};